import React from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '@/routes/paths';
import { useSession } from '@/hooks/useSession';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { checkPermission } from '@/hooks/usePermission';
import { canViewExercicios } from '@/lib/teacher-profile';
import { DashboardSection } from '@/components/custom/dashboard';
import desafioAoVivoIcon from '@/assets/images/icons/challenge-bolt-3d.png';
import desafioDaSemanaIcon from '@/assets/images/icons/desafio-da-semana.png';
import exerciciosIcon from '@/assets/images/icons/exercicios.png';
import planoIcon from '@/assets/images/icons/books-3d.png';
import atividadesIcon from '@/assets/images/icons/atividade-complementar.png';

// Mesma técnica de bevel 3D do botão-seta do HomeNavCard (box-shadow, não
// border-b-3 — esse token não existe no repo). Valores exatos do Figma — os
// nomes de variável de lá (--estudo-12, --warning-lighter, --secondary-lighter)
// não correspondem a nenhum token deste repo (nem no nome nem no valor: por
// ex. --color-secondary-lighter já existe mas é #C4B0EE no playful, não
// #DEF6F8), então ficou o hex literal em vez de forçar um token que já
// significa outra cor. `buttonBorder` é a cor da borda/sombra 3D do botão,
// não mais calculada por color-mix — o Figma já veio com a borda definida.
export const SHORTCUT_ACCENTS = {
  desafioAoVivo: { bg: '#6035A11F', button: '#462878', buttonBorder: '#2C1A50' }, // roxo
  desafioDaSemana: { bg: '#FFF5CC', button: '#FFAB00', buttonBorder: '#7A4100' }, // amarelo
  exercicios: { bg: 'var(--color-bg-teal-soft)', button: 'var(--color-third)', buttonBorder: 'var(--color-third-dark)' }, // ciano
  planoDeAula: { bg: '#DEF6F8', button: '#32C9D3', buttonBorder: '#009099' }, // ciano
  atividades: { bg: '#FFD7E3', button: '#FF0F57', buttonBorder: '#820027' } // rosa
};

function ShortcutActionCard({ to, illustration, title, subtitle, ctaLabel, accent }) {
  return (
    <div
      className="flex flex-col items-start gap-2 rounded-[16px] p-3"
      style={{ background: accent.bg }}
      data-testid="shortcut-card"
      data-title={title}
    >
      <img
        src={illustration}
        alt=""
        aria-hidden="true"
        className="h-12 w-12 object-contain drop-shadow-sm"
      />
      <div className="flex flex-col gap-0.5">
        <span
          className="block text-sm font-extrabold"
          style={{ color: '#222731' }}
        >
          {title}
        </span>
        <span className="line-clamp-2 block text-sm font-semibold text-neutral-600">{subtitle}</span>
      </div>

      <Link
        to={to}
        className="mt-1 flex h-8 shrink-0 items-center justify-center rounded-full px-3 text-xs font-bold text-white transition-transform duration-100 active:scale-[0.97]"
        style={{
          background: accent.button,
          boxShadow: `0 3px 0 ${accent.buttonBorder}, 0 4px 8px rgba(0,0,0,0.18)`
        }}
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

export function TeacherShortcuts() {
  const { session } = useSession();
  const { abilities } = usePlatformAbilities();

  // Mesma regra do item "Exercícios" em menu-admin.jsx, agora numa fonte
  // única (src/lib/teacher-profile.js) — não pode mais divergir do menu.
  const canSeeExercicios = canViewExercicios(session, abilities, checkPermission);

  const shortcuts = [
    {
      key: 'desafio-ao-vivo',
      to: PATHS.LIVE_CHALLENGE.ADMIN_CREATE('nome'),
      illustration: desafioAoVivoIcon,
      title: 'Desafio Ao Vivo',
      subtitle: 'Conduza disputas em tempo real',
      ctaLabel: 'Criar',
      accent: SHORTCUT_ACCENTS.desafioAoVivo,
      available: Boolean(abilities?.['desafio-ao-vivo'])
    },
    {
      key: 'desafio-da-semana',
      to: PATHS.WEEK_CHALLENGE.CREATE,
      illustration: desafioDaSemanaIcon,
      title: 'Desafio da Semana',
      subtitle: 'Lance o desafio e aguarde os resultados',
      ctaLabel: 'Criar',
      accent: SHORTCUT_ACCENTS.desafioDaSemana,
      available: Boolean(abilities?.['desafio-da-semana'])
    },
    {
      key: 'planos-de-aula',
      to: PATHS.LESSON_PLAN.START,
      illustration: planoIcon,
      title: 'Planos de Aula',
      subtitle: 'Monte e reaproveite seus planos',
      ctaLabel: 'Ver',
      accent: SHORTCUT_ACCENTS.planoDeAula,
      available: true
    },
    {
      key: 'atividades-complementares',
      to: PATHS.COMPLEMENTARY_ACTIVITY.LIST,
      illustration: atividadesIcon,
      title: 'Atividades complementares',
      subtitle: 'Ofereça atividades de estudo individuais',
      ctaLabel: 'Ver',
      accent: SHORTCUT_ACCENTS.atividades,
      available: Boolean(abilities?.['cursos-ativ-complementar'])
    }
  ].filter(shortcut => shortcut.available);

  if (shortcuts.length === 0) return null;

  // Número de colunas segue a quantidade real de atalhos disponíveis (varia
  // por ability) — sem isso, menos de 4 atalhos deixava célula(s) vazia(s)
  // no fim da grid em vez dos cards crescerem pra ocupar o espaço. Limitado a
  // 2 no mobile / 4 no xl porque são os máximos que a grid original suportava
  // (grid-cols-2 xl:grid-cols-4); classes precisam existir como string
  // literal aqui pro Tailwind JIT gerar o CSS.
  const MOBILE_GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2' };
  const DESKTOP_GRID_COLS = { 1: 'xl:grid-cols-1', 2: 'xl:grid-cols-2', 3: 'xl:grid-cols-3', 4: 'xl:grid-cols-4' };
  const mobileCols = Math.min(shortcuts.length, 2);
  const desktopCols = Math.min(shortcuts.length, 4);

  return (
    <DashboardSection>
      {/* xl, não lg: mesmo motivo do breakpoint no hero logo acima
          (dashboard-teacher-hero.jsx) — a sidebar da página consome boa
          largura a partir de lg, então os dois quebram juntos e as bordas
          continuam alinhadas quando há 4 atalhos (2 cards de 50% no hero bate
          exato com 4 de 25% aqui). */}
      <div className={`grid gap-5 ${MOBILE_GRID_COLS[mobileCols]} ${DESKTOP_GRID_COLS[desktopCols]}`}>
        {shortcuts.map(shortcut => (
          <ShortcutActionCard
            key={shortcut.key}
            to={shortcut.to}
            illustration={shortcut.illustration}
            title={shortcut.title}
            subtitle={shortcut.subtitle}
            ctaLabel={shortcut.ctaLabel}
            accent={shortcut.accent}
          />
        ))}
      </div>
    </DashboardSection>
  );
}
